import React, {PropsWithChildren, useEffect} from "react";
import {Box, ModalProps, Typography} from "@material-ui/core";
import Backdrop from "@material-ui/core/Backdrop";
import FfairPaper from "view/components/FfairPaper";
import Modal from "@material-ui/core/Modal";
import Fade from "@material-ui/core/Fade";
import clsx from "clsx";
import useEmbeddedChatActive from "hooks/useEmbeddedChatActive";
import useStyles from "./styles";

type FfairModalProps = {
  children: React.ReactNode;
  onClose?: () => void;
  isCalledByAnotherModal?: boolean;
  open?: boolean;
  reset?: () => void;
  className?: string;
  disableClose?: boolean;
  disableScrollLock?: boolean;
} & Pick<
  ModalProps,
  "disableEnforceFocus" | "disableEscapeKeyDown" | "onEscapeKeyDown"
>;

const FfairModal = ({
  open = true,
  onClose,
  isCalledByAnotherModal = false,
  children,
  reset,
  className,
  disableClose = false,
  disableScrollLock = false,
  disableEnforceFocus,
  disableEscapeKeyDown,
  onEscapeKeyDown,
}: FfairModalProps) => {
  const isEmbeddedChatActive = useEmbeddedChatActive();
  const handleClose = () => {
    if (disableClose) return;
    if (onClose) onClose();
  };

  const classes = useStyles();
  useEffect(() => {
    if (!open && reset) {
      reset();
    }
  }, [open, reset]);
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={clsx(classes.modal, className)}
      open={open}
      onClose={handleClose}
      disableBackdropClick={disableClose}
      disableScrollLock={disableScrollLock}
      disableEnforceFocus={disableEnforceFocus || isEmbeddedChatActive}
      disableEscapeKeyDown={disableEscapeKeyDown}
      onEscapeKeyDown={onEscapeKeyDown}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        className: !isCalledByAnotherModal
          ? classes.backdrop
          : classes.lightBackdrop,
      }}
    >
      <Fade in={open}>
        <div className={classes.contentWrapper}>{children}</div>
      </Fade>
    </Modal>
  );
};

export default FfairModal;

type BaseFfairModalComponentProps = {
  className?: string;
  style?: React.CSSProperties;
};
FfairModal.Title = function ({
  children,
  className,
  style,
}: PropsWithChildren<BaseFfairModalComponentProps>) {
  const classes = useStyles();

  return (
    <Typography className={clsx(classes.title, className)} style={style}>
      {children}
    </Typography>
  );
};

FfairModal.Subtitle = function ({
  children,
  className,
  style,
}: PropsWithChildren<BaseFfairModalComponentProps>) {
  const classes = useStyles();

  return (
    <Typography className={clsx(classes.subtitle, className)} style={style}>
      {children}
    </Typography>
  );
};

FfairModal.Paper = function ({
  children,
  className,
  style,
}: PropsWithChildren<BaseFfairModalComponentProps>) {
  const classes = useStyles();

  return (
    <FfairPaper className={clsx(classes.paper, className)} style={style}>
      {children}
    </FfairPaper>
  );
};

FfairModal.Header = function ({
  children,
  className,
  style,
}: PropsWithChildren<BaseFfairModalComponentProps>) {
  const classes = useStyles();

  return (
    <Box className={clsx(classes.header, className)} style={style}>
      {children}
    </Box>
  );
};
FfairModal.Footer = function ({
  children,
  className,
  style,
}: PropsWithChildren<BaseFfairModalComponentProps>) {
  const classes = useStyles();

  return (
    <Box className={clsx(classes.footer, className)} style={style}>
      {children}
    </Box>
  );
};

FfairModal.Body = function ({
  children,
  className,
  style,
}: PropsWithChildren<BaseFfairModalComponentProps>) {
  const classes = useStyles();

  return (
    <Box className={clsx(classes.body, className)} style={style}>
      {children}
    </Box>
  );
};
